// Import react elements
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";

// Import core functions
import { activateNewAccount, checkStoreAlias, getAccountCountries, getActivationDetails, getCurrentCountry } from "api/APICoreCalls";

// Import components
import CreateButton from "global/components/CreateButton";
import ToggleSwitch from "global/components/ToggleSwitch";
import { LoadingSpinner } from "global/components/loading_ui/LoadingSpinnerAnimation";

// Import utility functions
import { SetCookie } from "global/utilities/Cookies";
import { DisplayConfirmationDialog } from "global/utilities/DialogBox";

// Import css style sheet
import "../ActivateAccount.css";

const activation_steps = [

    {
        key: "locality",
        title: "Your Account",
        status: "Required",
    },
    {
        key: "store",
        title: "Online Store",
        status: "Optional",
    },
    {
        key: "payout_details",
        title: "Getting Paid",
        status: "Optional",
    },
    {
        key: "payment_details",
        title: "Making Payments",
        status: "Later",
    },
    {
        key: "subscription_plan",
        title: "Subscription Plan",
        status: "Later",
    },
    {
        key: "referral",
        title: "Affiliate Referral",
        status: "Optional",
    },
    {
        key: "review",
        title: "Review & Finish",
        status: "",
    },

];

const currency_options = [

    // Pound Sterling is supported as an account currency
    {
        code: "GBP",
        title: "Pound Sterling",
        symbol: "£",
    },

    // United States Dollar is supported as an account currency
    {
        code: "USD",
        title: "United States Dollar",
        symbol: "$",
    },

    // Euro is supported as an account currency
    {
        code: "EUR",
        title: "Euro",
        symbol: "€",
    },

];



const create_alias_suggestion = (value) => {

    // Build a suggested alias from the store name
    return value
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9-]/g, "-")
        .replace(/-+/g, "-")
        .replace(/^-|-$/g, "");

};



const validate_alias = (alias) => {

    // Empty aliases are allowed because the first store step is optional
    if (!alias) { return ""; }

    // Check the alias is long enough
    if (alias.length < 4) { return "Store URL alias must be at least 4 characters long."; }

    // Check the alias is not too long
    if (alias.length > 50) { return "Store URL alias cannot be more than 50 characters long."; }

    // Check the alias is lowercase
    if (alias !== alias.toLowerCase()) { return "Store URL alias must only use lowercase letters, numbers and hyphens."; }

    // Check the alias only uses valid characters
    if (!/^[a-z0-9-]+$/.test(alias)) { return "Store URL alias must only use lowercase letters, numbers and hyphens."; }

    // Check the alias does not start or end with a hyphen
    if (alias.startsWith("-") || alias.endsWith("-")) { return "Store URL alias cannot start or end with a hyphen."; }

    // Check the alias does not contain repeated hyphens
    if (alias.includes("--")) { return "Store URL alias cannot contain repeated hyphens."; }

    // Return no error
    return "";

};



const validate_email = (email_address) => {

    // Empty payout email addresses are allowed because the payout step is optional
    if (!email_address) { return ""; }

    // Check the email address looks valid enough for client-side validation
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email_address)) { return "Please enter a valid PayPal payout email address."; }

    // Return no error
    return "";

};



const ActivateAccount = () => {

    // Set location to useLocation() which gives access to the current URL/location object
    const location = useLocation();

    // Set navigate to useNavigate() which provides a function to programmatically navigate between routes
    const navigate = useNavigate();

    // Set the url parameters
    const url_params = useMemo(() => new URLSearchParams(location.search), [location.search]);

    // Set token to the one in the url
    const token = url_params.get("token");

    // Set email to the one in the url
    const email = url_params.get("email");

    // Store the current wizard step
    const [current_step, set_current_step] = useState("locality");

    // Store the countries which are currently available for accounts
    const [country_options, set_country_options] = useState([]);

    // Store the country detected from the browser IP
    const [detected_country, set_detected_country] = useState(null);

    // Store whether the detected country is available for account creation
    const [detected_country_supported, set_detected_country_supported] = useState(null);

    // Store whether all supported countries should be displayed
    const [show_all_countries, set_show_all_countries] = useState(false);

    // Store whether all currencies should be displayed
    const [show_all_currencies, set_show_all_currencies] = useState(false);

    // Store whether locality information is still loading
    const [locality_loading, set_locality_loading] = useState(true);

    // Store the selected country
    const [selected_country, set_selected_country] = useState("");

    // Store the selected currency
    const [selected_currency, set_selected_currency] = useState("");

    // Store the first store name
    const [store_name, set_store_name] = useState("");

    // Store the first store alias
    const [store_alias, set_store_alias] = useState("");

    // Store whether new designs should automatically be added to the marketplace
    const [store_marketplace_autoadd, set_store_marketplace_autoadd] = useState(true);

    // Store whether the alias has been manually changed
    const [store_alias_touched, set_store_alias_touched] = useState(false);

    // Store the PayPal payout email address
    const [paypal_payout_email, set_paypal_payout_email] = useState("");

    // Store the alias availability message
    const [store_alias_message, set_store_alias_message] = useState("");

    // Store whether the alias is available
    const [store_alias_available, set_store_alias_available] = useState(false);

    // Store the current loading state
    const [loading, set_loading] = useState(false);

    // Store any form error
    const [form_error, set_form_error] = useState("");

    // Store the referral attached to this account
    const [account_referral, set_account_referral] = useState(null);

    // Store whether the referral should remain active
    const [keep_referral, set_keep_referral] = useState(true);

    // Store whether the activation link has been validated
    const [activation_validated, set_activation_validated] = useState(false);

    // Load account countries, currencies and the browser location
    useEffect(() => {

        const load_locality_options = async () => {

            // Keep the locality step disabled until its supporting data has loaded
            set_locality_loading(true);

            // Load all required locality information together
            const [countries_response, location_response, activation_response] = await Promise.all([
                getAccountCountries(),
                getCurrentCountry(),
                getActivationDetails(email, token),
            ]);

            // Stop when the activation link is not valid
            if (!activation_response?.success) {
                set_form_error(activation_response?.error_message || "This activation link is not valid.");
                set_activation_validated(false);
                set_locality_loading(false);
                return;
            }

            // The activation link has been validated
            set_activation_validated(true);

            // Store any affiliate referral attached to this account
            const referral = activation_response.results?.referral || null;
            set_account_referral(referral);

            // Keep an active referral unless the user removes it during setup
            if (referral) {
                set_keep_referral(referral.active === "Y");
            }

            // Stop when countries could not be loaded because no safe account country can be selected
            if (!countries_response?.success || !Array.isArray(countries_response.results)) {

                set_form_error(countries_response?.error_message || "Account countries could not be loaded.");
                set_locality_loading(false);
                return;

            }

            // Store the supported account countries
            const supported_countries = countries_response.results;
            set_country_options(supported_countries);

            // IP lookup failure is non-fatal because the user can choose their country manually
            if (!location_response?.success || !location_response?.results?.country) {

                // No country could be detected
                set_detected_country(null);
                set_detected_country_supported(null);

                // The user must choose from the supported country list
                set_show_all_countries(true);

                // Keep alternative currencies hidden until a country has been selected
                set_show_all_currencies(false);

                // Locality information has finished loading
                set_locality_loading(false);

                return;

            }

            // Store the country detected from the browser IP
            const browser_country = location_response.results.country;
            set_detected_country(browser_country);

            // Match the detected country against countries explicitly enabled for accounts
            const supported_country = supported_countries.find((country) => {

                return String(country.code).toUpperCase() === String(browser_country.code).toUpperCase();

            });

            // Do not silently substitute another country when the detected country is unsupported
            if (!supported_country) {

                set_detected_country_supported(false);
                set_locality_loading(false);
                return;

            }

            // Check that the detected country's default currency is supported for accounts
            const default_currency = currency_options.find((currency) => currency.code === supported_country.default_currency);

            // Fail clearly if the country configuration uses an unsupported account currency
            if (!default_currency) {

                set_form_error("The default currency for " + supported_country.name + " is not currently supported.");
                set_locality_loading(false);

                return;

            }

            // The detected country is supported for account creation
            set_detected_country_supported(true);

            // Pre-select the supported version of the detected country
            set_selected_country(supported_country.code);

            // Pre-select the country's supported default currency
            set_selected_currency(default_currency.code);

            // Keep alternative countries and currencies hidden initially
            set_show_all_countries(false);
            set_show_all_currencies(false);

            // Locality information has finished loading
            set_locality_loading(false);

        };

        load_locality_options();

        // Only load the activation setup once when this page opens
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Start with the complete country list when alternatives are visible
    let visible_country_options = country_options;

    // When a supported country was detected, initially show only that selected country
    if (!show_all_countries && selected_country) {
        visible_country_options = country_options.filter((country) => country.code === selected_country);
    }

    // Hide all country choices when an unsupported country was detected until the user says it is incorrect
    if (!show_all_countries && detected_country_supported === false) {
        visible_country_options = [];
    }

    // Start with the complete currency list when alternatives are visible
    let visible_currency_options = currency_options;

    // Initially show only the selected/default currency
    if (!show_all_currencies && selected_currency) {
        visible_currency_options = currency_options.filter((currency) => currency.code === selected_currency);
    }

    // Do not offer currencies before an account country has been chosen
    if (!selected_country) {
        visible_currency_options = [];
    }

    // Get the selected country details
    const selected_country_details = country_options.find((country) => country.code === selected_country);

    // Get the selected currency details
    const selected_currency_details = currency_options.find((currency) => currency.code === selected_currency);

    // Check whether the activation link is valid enough to continue
    const has_activation_details = token && email && activation_validated;

    // Check whether the locality step is complete
    const locality_complete = selected_country !== "" && selected_currency !== "";

    // Build the active wizard steps
    const active_step_keys = [
        "locality",
        "store",
        "payout_details",
    ];

    // Include the referral step only when this account has a referral
    if (account_referral) {
        active_step_keys.push("referral");
    }

    // Review is always the final active step
    active_step_keys.push("review");

    // Get the current step index
    const current_step_index = active_step_keys.indexOf(current_step);

    // Work out the progress percentage
    const progress_percentage = Math.round(((current_step_index + 1) / active_step_keys.length) * 100);

    // Get the store alias error
    const store_alias_error = validate_alias(store_alias);

    // Get the payout email error
    const paypal_payout_email_error = validate_email(paypal_payout_email);

    // Set the page title for the current step
    const current_step_title = {
        locality: "Set up your StrichSuite account",
        store: "Set up your online store",
        payout_details: "Set up payout details",
        referral: "Affiliate referral",
        review: "Review and activate your account",
    }[current_step];

    // Set the page intro for the current step
    const current_step_intro = {
        locality: "Choose the country and currency for this account. These settings are mandatory and cannot be changed once the account has been activated.",
        store: "You can name your first hosted store now, or skip this and set it up later. You can change these settings later from the store settings page.",
        payout_details: "Add the PayPal email address you want to use for payouts, or skip this and add it later from your account settings.",
        referral: "Review the affiliate referral attached to your account before continuing.",
        review: "Check your account settings before activating your account.",
    }[current_step];

    // Handle selecting a country
    const handle_country_select = (country) => {

        // Check that this country's default currency is supported for accounts
        const default_currency = currency_options.find((currency) => currency.code === country.default_currency);

        // Do not select a country whose configured default currency is unsupported
        if (!default_currency) {

            set_form_error("The default currency for " + country.name + " is not currently supported.");

            return;

        }

        // Store the selected country
        set_selected_country(country.code);

        // Reset the account currency to this country's supported default currency
        set_selected_currency(default_currency.code);

        // Hide alternative currencies again after changing country
        set_show_all_currencies(false);

        // Clear the unsupported-location warning once the user deliberately corrects their country
        set_detected_country_supported(true);

        // Clear any existing form error
        set_form_error("");

    };



    // Handle selecting a currency
    const handle_currency_select = (currency) => {

        // Store the selected currency
        set_selected_currency(currency.code);

        // Clear any existing form error
        set_form_error("");

    };

    // Check whether the store alias is available
    const handle_store_alias_check = async (alias) => {

        // Clear the alias message if no alias has been entered
        if (!alias) {
            set_store_alias_message("");
            set_store_alias_available(false);
            return;
        }

        // Validate the alias locally first
        const alias_error = validate_alias(alias);

        // Show local validation errors without calling the API
        if (alias_error) {
            set_store_alias_message(alias_error);
            set_store_alias_available(false);
            return;
        }

        // Check the alias through the API
        const response = await checkStoreAlias(alias);

        // Show the API result
        if (response.success) {
            set_store_alias_message("This URL alias is available.");
            set_store_alias_available(true);
        } else {
            set_store_alias_message(response.error_message || "This URL alias is not available.");
            set_store_alias_available(false);
        }

    };

    // Handle changing the store name
    const handle_store_name_change = (event) => {

        // Pull out the new store name
        const new_store_name = event.target.value;

        // Store the new store name
        set_store_name(new_store_name);

        // Update the store alias suggestion unless the user has manually edited it
        if (!store_alias_touched) {

            // Create the suggested alias
            const suggested_alias = create_alias_suggestion(new_store_name);

            // Store the suggested alias
            set_store_alias(suggested_alias);

            // Check whether the suggested alias is available
            handle_store_alias_check(suggested_alias);

        }

        // Clear any existing form error
        set_form_error("");

    };



    // Handle changing the store alias
    const handle_store_alias_change = (event) => {

        // Store that the alias has been manually edited
        set_store_alias_touched(true);

        // Store the alias exactly as entered
        set_store_alias(event.target.value);

        // Check whether the alias is available
        handle_store_alias_check(event.target.value);

        // Clear any existing form error
        set_form_error("");

    };



    // Handle changing the payout email address
    const handle_paypal_payout_email_change = (event) => {

        // Store the PayPal payout email address
        set_paypal_payout_email(event.target.value);

        // Clear any existing form error
        set_form_error("");

    };



    // Move to a specific step
    const move_to_step = (step_key) => {

        // Clear any existing form error
        set_form_error("");

        // Move to the requested step
        set_current_step(step_key);

    };



    // Continue from locality
    const handle_locality_continue = () => {

        // Check whether the activation link has the required values
        if (!has_activation_details) {

            set_form_error("This activation link is missing the account email address or activation token.");

            return;

        }

        // Check whether the required locality values have been selected
        if (!locality_complete) {

            set_form_error("Please choose your country and currency before continuing.");

            return;

        }

        // Move to the store step
        move_to_step("store");

    };



    // Continue from store
    const handle_store_continue = () => {

        // Check the store alias if one has been entered
        if (store_alias_error) {

            set_form_error(store_alias_error);

            return;

        }

        // Check whether the entered alias is available
        if (store_alias.trim() !== "" && !store_alias_available) {

            set_form_error(store_alias_message || "Please choose an available store URL alias.");

            return;

        }

        // Move to the payout details step
        move_to_step("payout_details");

    };



    // Continue from payout details
    const handle_payout_continue = () => {

        // Check the payout email address if one has been entered
        if (paypal_payout_email_error) {

            set_form_error(paypal_payout_email_error);

            return;

        }

        // Show the referral step when this account has a referral
        if (account_referral) {
            move_to_step("referral");
            return;
        }

        // Move directly to review when there is no referral
        move_to_step("review");

    };



    // Submit the activation request
    const handle_activate_account = async () => {

        // Check whether the activation link has the required values
        if (!has_activation_details) {

            set_form_error("This activation link is missing the account email address or activation token.");

            return;

        }

        // Check whether the required locality values have been selected
        if (!locality_complete) {

            set_form_error("Please choose your country and currency before activating your account.");

            set_current_step("locality");

            return;

        }

        // Check the store alias if one has been entered
        if (store_alias_error) {

            set_form_error(store_alias_error);

            set_current_step("store");

            return;

        }

        // Check the payout email address if one has been entered
        if (paypal_payout_email_error) {

            set_form_error(paypal_payout_email_error);

            set_current_step("payout_details");

            return;

        }

        // Set loading to true
        set_loading(true);

        // Clear any existing form error
        set_form_error("");

        // Build the activation payload
        const postdata = {
            country: selected_country,
            currency: selected_currency,
        };

        // Remove the stored referral when the user opted out during setup
        if (account_referral && !keep_referral) {
            postdata.remove_referral = "Y";
        }

        // Add the optional store name
        if (store_name.trim() !== "") { postdata.store_name = store_name.trim(); }

        // Add the optional store alias
        if (store_alias.trim() !== "") { postdata.store_alias = store_alias.trim(); }

        // Add the initial store marketplace preference
        postdata.store_marketplace_autoadd = store_marketplace_autoadd ? "Y" : "N";

        // Add the optional PayPal payout email address
        if (paypal_payout_email.trim() !== "") { postdata.paypal_payout_email = paypal_payout_email.trim(); }

        // Run the activation API call
        const response = await activateNewAccount(email, token, postdata);

        // Set loading to false
        set_loading(false);

        // Check whether the activation succeeded
        if (response.success) {

            // Set the prefill_email cookie to the url email
            SetCookie("prefill_email", email, 1);

            // Display a confirmation dialog
            DisplayConfirmationDialog(

                // Dialog message
                "Account activated, please log in.",

                // Confirm button title
                "OK",

                // Cancel button title - empty because we don't want a cancel button
                "",

                // Returned response from the dialog box
                function (is_confirmed) {

                    // Check if the response is TRUE
                    if (is_confirmed) {

                        // Navigate to the login page
                        navigate("/login");

                    }

                }
            );

            return;

        }

        // Show the returned error message
        set_form_error("Account has not been activated: " + (response.error_message || response.error || "Unknown error"));

    };



    // Render a selectable option card
    const render_option_card = (option, selected, on_select, option_type) => {

        return (

            <button
                key={option.code}
                type="button"
                className={`activate_option_card ${selected ? "activate_option_card_selected" : ""}`}
                onClick={() => on_select(option)}
            >

                <span className="activate_option_image_wrap">

                    {/* Country flags are SVG markup supplied by the StrichSuite country data */}
                    {option_type === "country" && option.flag_svg &&
                        <span
                            className="activate_option_flag"
                            dangerouslySetInnerHTML={{ __html: option.flag_svg }}
                        />
                    }

                    {/* Currency cards use the currency symbol supplied by the API */}
                    {option_type === "currency" &&
                        <span className="activate_option_currency_symbol">{option.symbol || option.code}</span>
                    }

                </span>

                <span className="activate_option_text">

                    <span className="activate_option_title">{option.title || option.name}</span>

                    <span className="activate_option_subtitle">{option.code}</span>

                </span>

            </button>

        );

    };

    // Render a placeholder option card while locality data is loading
    const render_loading_option_card = (message) => {

        return (

            <div className="activate_option_card activate_option_card_loading">

                <LoadingSpinner message={message} />

            </div>

        );

    };

    return (

        <div className="activate_page">

            <div className="activate_sidebar">

                <a href="/" >

                    <div className="activate_logo-container">

                        <img src="/images/StrichSuite-logo-tagline.png" alt="StrichSuite" />

                    </div>

                </a>

                <div className="activate_progress_panel">

                    <div className="activate_progress_header">

                        <span>Setup Progress</span>

                        <strong>{progress_percentage}%</strong>

                    </div>

                    <div className="activate_progress_bar">

                        <div className="activate_progress_bar_fill" style={{ width: `${progress_percentage}%` }}></div>

                    </div>

                    <div className="activate_steps">

                        {activation_steps
                            .filter((step) => step.key !== "referral" || account_referral)
                            .map((step) => (

                                <div key={step.key} className={`activate_step ${current_step === step.key ? "activate_step_active" : ""} ${!active_step_keys.includes(step.key) ? "activate_step_future" : ""}`}>

                                    <span className="activate_step_dot"></span>

                                    <span className="activate_step_text">

                                        <span className="activate_step_title">{step.title}</span>

                                        <span className="activate_step_status">{step.status}</span>

                                    </span>

                                </div>

                            ))}

                    </div>

                </div>

            </div>

            <div className="activate_content">

                <div className="activate_card">

                    <div className="activate_card_header">

                        <p className="activate_kicker">Account Activation</p>

                        <h1>{current_step_title}</h1>

                        <p className="activate_intro">{current_step_intro}</p>

                    </div>

                    {(!token || !email) && (

                        <div className="activate_warning">

                            This activation link is missing the account email address or activation token.

                        </div>

                    )}

                    {current_step === "locality" && (

                        <div className="activate_step_body">

                            {!locality_loading && detected_country && detected_country_supported === false &&

                                <div className="activate_warning">

                                    We detected that you are in <b>{detected_country.name}</b>. StrichSuite accounts are not currently available in this country.

                                    <div className="activate_location_action">

                                        <CreateButton
                                            title="This isn't my country"
                                            size="s"
                                            invert={true}
                                            onClick={() => {
                                                set_show_all_countries(true);
                                                set_detected_country_supported(true);
                                            }}
                                        />

                                    </div>

                                </div>

                            }

                            <div className="activate_section">

                                <h2>Country</h2>

                                <p>
                                    Choose the country where you or your business are actually based.
                                    <b> This cannot be changed after the account is activated</b>, and we may need to verify it later.
                                </p>

                                <div className="activate_option_grid">

                                    {locality_loading
                                        ? render_loading_option_card("Loading countries...")
                                        : visible_country_options.map((country) => render_option_card(country, selected_country === country.code, handle_country_select, "country"))}

                                </div>

                                <div className="activate_expand_slot">

                                    {!locality_loading && selected_country && !show_all_countries &&

                                        <button
                                            type="button"
                                            className="activate_expand_button"
                                            onClick={() => set_show_all_countries(true)}
                                        >
                                            This isn't my country
                                        </button>

                                    }

                                </div>

                            </div>

                            {(locality_loading || selected_country) &&

                                <div className="activate_section">

                                    <h2>Currency</h2>

                                    <p>
                                        This is the account currency used by StrichSuite for pricing, payments and payouts.
                                        <b> It cannot be changed after activation.</b>
                                    </p>

                                    <div className="activate_option_grid">

                                        {locality_loading
                                            ? render_loading_option_card("Loading currencies...")
                                            : visible_currency_options.map((currency) => render_option_card(currency, selected_currency === currency.code, handle_currency_select, "currency"))}

                                    </div>

                                    <div className="activate_expand_slot">

                                        {!locality_loading && selected_currency && !show_all_currencies && currency_options.length > 1 &&

                                            <button
                                                type="button"
                                                className="activate_expand_button"
                                                onClick={() => set_show_all_currencies(true)}
                                            >
                                                Choose a different currency
                                            </button>

                                        }

                                    </div>

                                </div>

                            }

                            <div className="activate_locked_notice">

                                <b>Important:</b> Your country and currency become permanent account settings when you activate the account. Choose the country where you are genuinely based. If we later need to verify your country, the details you provide must support the country selected here.

                            </div>

                            {form_error && <div className="activate_error">{form_error}</div>}

                            <div className="activate_actions">

                                <CreateButton title="Continue" size="m" onClick={handle_locality_continue} disabled={locality_loading || !locality_complete || !has_activation_details} />

                            </div>

                        </div>

                    )}

                    {current_step === "store" && (

                        <div className="activate_step_body">

                            <div className="activate_section">

                                <label className="activate_field_label" htmlFor="store_name">Store name</label>

                                <input
                                    id="store_name"
                                    className="activate_text_input"
                                    type="text"
                                    value={store_name}
                                    onChange={handle_store_name_change}
                                    placeholder="e.g. My Strich Store"
                                />

                                <label className="activate_field_label" htmlFor="store_alias">Store URL alias</label>

                                <div className="activate_alias_row">

                                    <div className="activate_url_input_wrap">

                                        <span>https://strich.store/</span>

                                        <input
                                            id="store_alias"
                                            className="activate_text_input activate_url_input"
                                            type="text"
                                            value={store_alias}
                                            onChange={handle_store_alias_change}
                                            placeholder="e.g. my-strich-store"
                                        />

                                    </div>

                                    <div className="activate_alias_message_wrap">

                                        {store_alias_message && (

                                            <span className={`activate_alias_message ${store_alias_available ? "activate_alias_message_success" : "activate_alias_message_error"}`}>

                                                {store_alias_available ? "✔" : "✘"} {store_alias_message}

                                            </span>

                                        )}

                                    </div>

                                </div>

                                <p className="activate_field_hint">Use 4-50 lowercase letters, numbers and hyphens. The alias can be changed later, and cannot already be in use.</p>

                                <div className="activate_toggle_row">

                                    <ToggleSwitch
                                        name="store_marketplace_autoadd"
                                        value={store_marketplace_autoadd}
                                        onChange={(event) => set_store_marketplace_autoadd(event.target.value)}
                                        size="s"
                                    />

                                    <div className="activate_toggle_text">

                                        <span>Automatically add new designs from this store to the Strich marketplace</span>

                                    </div>

                                </div>

                            </div>

                            {form_error && <div className="activate_error">{form_error}</div>}

                            <div className="activate_actions">

                                <CreateButton title="Back" size="m" invert={true} onClick={() => move_to_step("locality")} />

                                <CreateButton title="Skip" size="m" invert={true} onClick={() => move_to_step("payout_details")} />

                                <CreateButton title="Continue" size="m" onClick={handle_store_continue} disabled={store_alias_error !== "" || (store_alias.trim() !== "" && !store_alias_available)} />

                            </div>

                        </div>

                    )}

                    {current_step === "payout_details" && (

                        <div className="activate_step_body">

                            <div className="activate_section">

                                <label className="activate_field_label" htmlFor="paypal_payout_email">PayPal payout email address</label>

                                <input
                                    id="paypal_payout_email"
                                    className="activate_text_input"
                                    type="email"
                                    value={paypal_payout_email}
                                    onChange={handle_paypal_payout_email_change}
                                    placeholder="e.g. payouts@example.com"
                                />

                                <p className="activate_field_hint">Payouts are currently made via PayPal. You will require a PayPal account to withdraw your earnings.</p>

                                {paypal_payout_email_error && <div className="activate_error">{paypal_payout_email_error}</div>}

                            </div>

                            {form_error && <div className="activate_error">{form_error}</div>}

                            <div className="activate_actions">

                                <CreateButton title="Back" size="m" invert={true} onClick={() => move_to_step("store")} />

                                <CreateButton
                                    title="Skip"
                                    size="m"
                                    invert={true}
                                    onClick={() => move_to_step(account_referral ? "referral" : "review")}
                                />

                                <CreateButton title="Continue" size="m" onClick={handle_payout_continue} disabled={paypal_payout_email_error !== ""} />

                            </div>

                        </div>

                    )}

                    {current_step === "referral" && account_referral && (

                        <div className="activate_step_body">

                            <div className="activate_section">

                                <h2>You were referred by {account_referral.display_name || account_referral.affiliate_id}</h2>

                                <p>
                                    After following a link containing the affiliate ID <b>{account_referral.affiliate_id}</b>.
                                </p>

                                <p>
                                    While this referral remains active, the affiliate may earn commission from qualifying activity on your account.
                                    This can include qualifying product fulfilment charges and subscription fees you pay to Strich.
                                </p>

                                <p>
                                    The referral does not increase the prices you pay, does not reduce your own earnings and does not give the affiliate access to your account or private account information.
                                </p>

                                <p>
                                    If you do not recognise this referral, or do not want the affiliate to earn commission from your future qualifying activity, you can remove it below. This cannot be undone.
                                </p>

                                <p>
                                    If you don't mind them earning from referring you, please leave it active. You will also be able to refer people and earn from those referrals yourself when your account is created.
                                </p>

                                <p>
                                    You can remove this referral from your account at any time.
                                </p>

                                <div className="activate_toggle_row">

                                    <ToggleSwitch
                                        name="keep_referral"
                                        value={keep_referral}
                                        onChange={(event) => set_keep_referral(event.target.value)}
                                        size="s"
                                    />

                                    <div className="activate_toggle_text">

                                        <span>Keep this affiliate referral active</span>

                                    </div>

                                </div>

                            </div>

                            {form_error && <div className="activate_error">{form_error}</div>}

                            <div className="activate_actions">

                                <CreateButton title="Back" size="m" invert={true} onClick={() => move_to_step("payout_details")} />

                                <CreateButton title="Continue" size="m" onClick={() => move_to_step("review")} />

                            </div>

                        </div>

                    )}

                    {current_step === "review" && (

                        <div className="activate_step_body">

                            <div className="activate_review_box">

                                <h2>Review your activation settings</h2>

                                <div className="activate_review_row">

                                    <span>Email</span>

                                    <strong>{email}</strong>

                                </div>

                                <div className="activate_review_row">

                                    <span>Country</span>

                                    <strong>{selected_country_details?.name}</strong>

                                </div>

                                <div className="activate_review_row">

                                    <span>Currency</span>

                                    <strong>{selected_currency_details?.title}</strong>

                                </div>

                                <div className="activate_review_row">

                                    <span>Store name</span>

                                    <strong>{store_name.trim() || "Set up later"}</strong>

                                </div>

                                <div className="activate_review_row">

                                    <span>Store URL alias</span>

                                    <strong>{store_alias.trim() || "Set up later"}</strong>

                                </div>

                                <div className="activate_review_row">

                                    <span>Auto-add designs to marketplace</span>

                                    <strong>{store_marketplace_autoadd ? "Yes" : "No"}</strong>

                                </div>

                                <div className="activate_review_row">

                                    <span>PayPal payout email</span>

                                    <strong>{paypal_payout_email.trim() || "Set up later"}</strong>

                                </div>

                                {account_referral &&

                                    <div className="activate_review_row">

                                        <span>Affiliate referral</span>

                                        <strong>
                                            {keep_referral
                                                ? `${account_referral.display_name || account_referral.affiliate_id} (${account_referral.affiliate_id})`
                                                : "Remove referral"}
                                        </strong>

                                    </div>

                                }

                            </div>

                            <div className="activate_locked_notice">

                                Country and currency cannot be changed after activation. Other settings can be changed later.

                            </div>

                            {form_error && <div className="activate_error">{form_error}</div>}

                            <div className="activate_actions">

                                <CreateButton
                                    title="Back"
                                    size="m"
                                    invert={true}
                                    onClick={() => move_to_step(account_referral ? "referral" : "payout_details")}
                                    disabled={loading}
                                />

                                <CreateButton title={loading ? "Activating..." : "Activate Account"} size="m" onClick={handle_activate_account} disabled={loading || !locality_complete || !has_activation_details} />

                            </div>

                        </div>

                    )}

                </div>

            </div>

        </div>

    );

};

export default ActivateAccount;